/**
 * Standalone Product Image Zoom Overlay
 * Works with PageFly galleries and standard Shopify product pages.
 * Click any product image → full-screen overlay with zoom in/out, pan, pinch, close.
 */
(function () {
  'use strict';

  var MIN_SCALE = 1;
  var MAX_SCALE = 5;
  var ZOOM_STEP = 0.5;
  var overlay = null;
  var zoomImg = null;
  var scale = 1;
  var translateX = 0;
  var translateY = 0;
  var lastTranslateX = 0;
  var lastTranslateY = 0;
  var isPanning = false;
  var startX = 0;
  var startY = 0;
  var initialPinchDistance = 0;
  var initialPinchScale = 1;
  var allImages = [];
  var currentIndex = 0;

  // ── Build the overlay DOM (once) ──
  function buildOverlay() {
    if (overlay) return;

    overlay = document.createElement('div');
    overlay.id = 'pf-zoom-overlay';
    overlay.innerHTML =
      '<div id="pf-zoom-controls">' +
        '<button type="button" id="pf-zoom-prev" aria-label="Previous image" title="Previous image">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>' +
        '</button>' +
        '<button type="button" id="pf-zoom-in" aria-label="Zoom in" title="Zoom in">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>' +
        '</button>' +
        '<button type="button" id="pf-zoom-out" aria-label="Zoom out" title="Zoom out">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>' +
        '</button>' +
        '<button type="button" id="pf-zoom-next" aria-label="Next image" title="Next image">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>' +
        '</button>' +
        '<button type="button" id="pf-zoom-close" aria-label="Close" title="Close">' +
          '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
      '</div>' +
      '<div id="pf-zoom-img-wrapper">' +
        '<img id="pf-zoom-img" src="" alt="Zoomed product image" draggable="false" />' +
      '</div>';

    document.body.appendChild(overlay);

    // Cache reference
    zoomImg = document.getElementById('pf-zoom-img');

    // ── Button events ──
    document.getElementById('pf-zoom-close').addEventListener('click', closeOverlay);
    document.getElementById('pf-zoom-in').addEventListener('click', function (e) { e.stopPropagation(); zoomTo(scale + ZOOM_STEP); });
    document.getElementById('pf-zoom-out').addEventListener('click', function (e) { e.stopPropagation(); zoomTo(scale - ZOOM_STEP); });
    document.getElementById('pf-zoom-prev').addEventListener('click', function (e) { e.stopPropagation(); navigateImage(-1); });
    document.getElementById('pf-zoom-next').addEventListener('click', function (e) { e.stopPropagation(); navigateImage(1); });

    // Close on backdrop click (only if not zoomed)
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target.id === 'pf-zoom-img-wrapper') {
        if (scale <= 1) closeOverlay();
      }
    });

    // Keyboard
    document.addEventListener('keydown', function (e) {
      if (!overlay || !overlay.classList.contains('pf-zoom-open')) return;
      if (e.key === 'Escape') closeOverlay();
      if (e.key === 'ArrowLeft') navigateImage(-1);
      if (e.key === 'ArrowRight') navigateImage(1);
      if (e.key === '+' || e.key === '=') zoomTo(scale + ZOOM_STEP);
      if (e.key === '-') zoomTo(scale - ZOOM_STEP);
    });

    // Mouse wheel
    var wrapper = document.getElementById('pf-zoom-img-wrapper');
    wrapper.addEventListener('wheel', function (e) {
      e.preventDefault();
      var delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      zoomTo(scale + delta);
    }, { passive: false });

    // ── Mouse pan ──
    wrapper.addEventListener('mousedown', function (e) {
      if (scale <= 1) return;
      isPanning = true;
      startX = e.clientX;
      startY = e.clientY;
      zoomImg.style.cursor = 'grabbing';
      e.preventDefault();
    });
    document.addEventListener('mousemove', function (e) {
      if (!isPanning) return;
      translateX = lastTranslateX + (e.clientX - startX);
      translateY = lastTranslateY + (e.clientY - startY);
      applyTransform();
    });
    document.addEventListener('mouseup', function () {
      if (!isPanning) return;
      isPanning = false;
      lastTranslateX = translateX;
      lastTranslateY = translateY;
      zoomImg.style.cursor = scale > 1 ? 'grab' : 'zoom-in';
    });

    // ── Touch: pinch + pan ──
    wrapper.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) {
        initialPinchDistance = getDistance(e.touches[0], e.touches[1]);
        initialPinchScale = scale;
        e.preventDefault();
      } else if (e.touches.length === 1 && scale > 1) {
        isPanning = true;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        e.preventDefault();
      }
    }, { passive: false });

    wrapper.addEventListener('touchmove', function (e) {
      if (e.touches.length === 2) {
        var d = getDistance(e.touches[0], e.touches[1]);
        zoomTo(initialPinchScale * (d / initialPinchDistance));
        e.preventDefault();
      } else if (e.touches.length === 1 && isPanning) {
        translateX = lastTranslateX + (e.touches[0].clientX - startX);
        translateY = lastTranslateY + (e.touches[0].clientY - startY);
        applyTransform();
        e.preventDefault();
      }
    }, { passive: false });

    wrapper.addEventListener('touchend', function (e) {
      if (e.touches.length < 2) initialPinchDistance = 0;
      if (e.touches.length === 0 && isPanning) {
        isPanning = false;
        lastTranslateX = translateX;
        lastTranslateY = translateY;
      }
    });

    // ── Double-click / double-tap ──
    var lastTap = 0;
    wrapper.addEventListener('click', function (e) {
      if (e.target.closest('#pf-zoom-controls')) return;
      var now = Date.now();
      if (now - lastTap < 350) {
        if (scale > 1) { resetZoom(); } else { zoomTo(2.5); }
        e.preventDefault();
      }
      lastTap = now;
    });
  }

  // ── Inject CSS ──
  function injectStyles() {
    if (document.getElementById('pf-zoom-styles')) return;
    var style = document.createElement('style');
    style.id = 'pf-zoom-styles';
    style.textContent =
      '#pf-zoom-overlay{display:none;position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,.92);z-index:99999;flex-direction:column;align-items:center;justify-content:center}' +
      '#pf-zoom-overlay.pf-zoom-open{display:flex}' +
      '#pf-zoom-controls{position:fixed;top:16px;right:16px;z-index:100000;display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.95);border-radius:30px;padding:4px 6px;box-shadow:0 2px 12px rgba(0,0,0,.25)}' +
      '#pf-zoom-controls button{background:transparent;border:none;border-radius:50%;color:#333;cursor:pointer;display:flex;align-items:center;justify-content:center;width:38px;height:38px;padding:0;transition:background .15s,color .15s}' +
      '#pf-zoom-controls button:hover{background:rgba(0,0,0,.08);color:#000}' +
      '#pf-zoom-close{border-left:1px solid rgba(0,0,0,.12)!important;border-radius:0 50% 50% 0!important;padding-left:2px!important}' +
      '#pf-zoom-img-wrapper{width:100%;height:100%;display:flex;align-items:center;justify-content:center;overflow:hidden;cursor:zoom-in}' +
      '#pf-zoom-img{max-width:90vw;max-height:90vh;object-fit:contain;transition:transform .2s ease;will-change:transform;user-select:none;-webkit-user-select:none;-webkit-user-drag:none;pointer-events:auto}' +
      /* Make PageFly main images show pointer cursor */
      '.pf-slide-main-media img,.product__modal-opener--image img{cursor:zoom-in}' +
      /* Counter badge */
      '#pf-zoom-counter{position:fixed;bottom:20px;left:50%;transform:translateX(-50%);color:#fff;font-size:14px;font-family:sans-serif;background:rgba(0,0,0,.5);padding:4px 14px;border-radius:20px;z-index:100000}';
    document.head.appendChild(style);
  }

  // ── Core zoom logic ──
  function applyTransform() {
    if (!zoomImg) return;
    zoomImg.style.transform = 'translate(' + translateX + 'px,' + translateY + 'px) scale(' + scale + ')';
  }

  function resetZoom() {
    scale = 1;
    translateX = 0;
    translateY = 0;
    lastTranslateX = 0;
    lastTranslateY = 0;
    applyTransform();
    if (zoomImg) zoomImg.style.cursor = 'zoom-in';
    updateButtons();
  }

  function zoomTo(newScale) {
    scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale));
    if (scale === 1) {
      translateX = 0; translateY = 0;
      lastTranslateX = 0; lastTranslateY = 0;
    }
    applyTransform();
    if (zoomImg) zoomImg.style.cursor = scale > 1 ? 'grab' : 'zoom-in';
    updateButtons();
  }

  function updateButtons() {
    var zi = document.getElementById('pf-zoom-in');
    var zo = document.getElementById('pf-zoom-out');
    if (zi) zi.style.opacity = scale >= MAX_SCALE ? '0.35' : '1';
    if (zo) zo.style.opacity = scale <= MIN_SCALE ? '0.35' : '1';
  }

  function getDistance(t1, t2) {
    var dx = t1.clientX - t2.clientX;
    var dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // ── Navigate between images ──
  function navigateImage(dir) {
    if (allImages.length <= 1) return;
    currentIndex = (currentIndex + dir + allImages.length) % allImages.length;
    loadImage(allImages[currentIndex]);
    resetZoom();
    updateCounter();
  }

  function loadImage(src) {
    if (!zoomImg) return;
    // Use high-res version
    var highRes = src.replace(/&width=\d+/, '&width=2048');
    if (highRes.indexOf('&width=') === -1 && highRes.indexOf('?') !== -1) {
      highRes += '&width=2048';
    }
    zoomImg.src = highRes;
  }

  function updateCounter() {
    var counter = document.getElementById('pf-zoom-counter');
    if (!counter) {
      counter = document.createElement('div');
      counter.id = 'pf-zoom-counter';
      overlay.appendChild(counter);
    }
    counter.textContent = (currentIndex + 1) + ' / ' + allImages.length;
    // Hide nav arrows if only one image
    var prev = document.getElementById('pf-zoom-prev');
    var next = document.getElementById('pf-zoom-next');
    if (prev) prev.style.display = allImages.length <= 1 ? 'none' : '';
    if (next) next.style.display = allImages.length <= 1 ? 'none' : '';
  }

  // ── Open / Close ──
  function openOverlay(imageSrc, imageList, index) {
    buildOverlay();
    allImages = imageList || [imageSrc];
    currentIndex = index || 0;
    loadImage(allImages[currentIndex]);
    resetZoom();
    updateCounter();
    overlay.classList.add('pf-zoom-open');
    document.body.style.overflow = 'hidden';
  }

  function closeOverlay() {
    if (!overlay) return;
    overlay.classList.remove('pf-zoom-open');
    document.body.style.overflow = '';
    resetZoom();
  }

  // ── Collect images from a gallery context ──
  function collectImages(clickedImg) {
    var images = [];
    var clickedSrc = clickedImg.src || '';
    var index = 0;

    // PageFly gallery: look for sibling slides
    var pfGallery = clickedImg.closest('.pf-media-slider');
    if (pfGallery) {
      var slides = pfGallery.querySelectorAll('.pf-slide-main-media img');
      slides.forEach(function (img, i) {
        images.push(img.src);
        if (img === clickedImg || img.src === clickedSrc) index = i;
      });
    }

    // Standard Shopify gallery
    if (images.length === 0) {
      var gallery = clickedImg.closest('media-gallery, .product__media-list');
      if (gallery) {
        var items = gallery.querySelectorAll('.product__media img');
        items.forEach(function (img, i) {
          images.push(img.src);
          if (img === clickedImg || img.src === clickedSrc) index = i;
        });
      }
    }

    // Fallback: just use the clicked image
    if (images.length === 0) {
      images.push(clickedSrc);
      index = 0;
    }

    return { images: images, index: index };
  }

  // ── Attach click handlers ──
  function init() {
    injectStyles();

    // Delegate click on product images
    document.addEventListener('click', function (e) {
      // Skip if overlay is open
      if (overlay && overlay.classList.contains('pf-zoom-open')) return;

      var img = null;

      // PageFly main media images (the large slider images)
      var pfSlide = e.target.closest('.pf-slide-main-media');
      if (pfSlide) {
        img = pfSlide.querySelector('img');
      }

      // Standard Shopify: clicking the modal-opener button on image type
      if (!img) {
        var opener = e.target.closest('.product__modal-opener--image');
        if (opener) {
          img = opener.querySelector('img');
          // Prevent the default lightbox modal from opening
          e.preventDefault();
          e.stopPropagation();
        }
      }

      if (img) {
        var result = collectImages(img);
        openOverlay(img.src, result.images, result.index);
        e.preventDefault();
        e.stopPropagation();
      }
    }, true); // Use capture phase to intercept before other handlers
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
